'use client'

import { Button, Card, CardContent, CardHeader, CardTitle, RadioGroup, RadioGroupItem } from '@chain-zen/ui'
import React from 'react'

import { useUserQuery } from '../../hooks/use-user-query'
import type { IWorkspaceMember } from '../../types/user'

interface ISelectEmailOrPhoneProps {
  carrierWorkspaceDisplayName: string | null | undefined
  getVerificationCodeAction: () => void
  gettingCode: boolean
  selectedMemberID: string | null
  setSelectedMemberIDAction: (value: string) => void
  workspaceMembers: IWorkspaceMember[]
}

/**
 * SelectEmailOrPhone renders a list of emails or phones and enables user selection
 */
export const SelectEmailOrPhone = ({
  carrierWorkspaceDisplayName = '',
  getVerificationCodeAction,
  gettingCode,
  selectedMemberID,
  setSelectedMemberIDAction,
  workspaceMembers
}: ISelectEmailOrPhoneProps) => {
  const { myDetails: userDetails } = useUserQuery()
  const userContact = userDetails?.email || userDetails?.phone || 'your contact'

  return (
    <Card className="border-none shadow-none">
      <CardHeader className="px-6 py-4 space-y-1">
        <CardTitle>Request to be added to {carrierWorkspaceDisplayName || 'workspace'}</CardTitle>
        <p className="text-sm text-muted-foreground">
          You must be part of {carrierWorkspaceDisplayName || 'this workspace'} to bid on this load, but{' '}
          <span className="font-medium text-foreground">{userContact}</span> is not registered for this company. To
          complete your registration, please send a verification code to one of the following emails:
        </p>
      </CardHeader>
      <CardContent className="px-6 space-y-4">
        <RadioGroup value={selectedMemberID || ''} onValueChange={setSelectedMemberIDAction} className="space-y-2">
          <div className="max-h-96 overflow-y-auto pr-1">
            {workspaceMembers.map((member, index) => {
              const userName = member?.user?.name
              const userEmail = member?.user?.email
              const userAccess = member?.accessType

              if (!userEmail) return null

              return (
                <div key={index} className="flex items-start space-x-3 py-2">
                  <RadioGroupItem value={member.user?.id || ''} id={`member-${index}`} />
                  <label
                    htmlFor={`member-${index}`}
                    className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {!!userName && (
                      <>
                        <span className="font-medium">{userName} </span>
                        {!!userAccess && <span className="font-medium">({userAccess}) </span>}
                        at{' '}
                      </>
                    )}
                    {userEmail}
                  </label>
                </div>
              )
            })}
          </div>
        </RadioGroup>

        <Button
          variant="default"
          disabled={!selectedMemberID}
          onClick={getVerificationCodeAction}
          loading={gettingCode}
          className="w-full"
        >
          {gettingCode ? 'Sending...' : 'Submit'}
        </Button>
      </CardContent>
    </Card>
  )
}
